import React, {
  forwardRef,
  InputHTMLAttributes,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { IconBaseProps } from 'react-icons';
import { FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';

import ComponentIsVisible from 'components/utils/IsVisible';

import {
  Container,
  Error,
  FieldContainer,
  IFieldContainerProps,
  IInputProps,
  Input,
  Label,
  ToggleSecure,
} from './styles';

export interface IComponentInputBaseProps
  extends Omit<IFieldContainerProps, 'isErrored' | 'isFocused' | 'isFilled'>,
    IInputProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'height'> {
  errorMessage?: string;
  hasError?: boolean;
  icon?: React.ComponentType<IconBaseProps>;
  isLoading?: boolean;
  label?: string;
  secure?: boolean;
  title?: string;
}

export interface IComponentInputBaseRefProps {
  setValueRef(text: string): void;
}

const ComponentInputBase: React.ForwardRefRenderFunction<
  IComponentInputBaseRefProps,
  IComponentInputBaseProps
> = (
  {
    backgroundColor,
    errorMessage,
    hasError = false,
    height,
    icon: IconInput,
    isLoading = false,
    label,
    onChange,
    readOnly = false,
    secure = false,
    textAlign,
    title,
    type = 'text',
    ...rest
  },
  ref,
) => {
  const [inputType, setInputType] = useState<string>(type);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isFilled, setIsFilled] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    if (inputRef.current) {
      inputRef.current.value = text;
    }
    if (onChange) {
      onChange(event);
    }
  };
  const handleInputFocus = () => {
    setIsFocused(true);
  };
  const handleInputBlur = () => {
    setIsFocused(false);
    setIsFilled(!!inputRef.current?.value);
  };
  const handleToggleSecure = () => {
    setInputType(currentType => {
      if (currentType === 'password') {
        return 'text';
      }
      return 'password';
    });
  };

  useImperativeHandle(ref, () => ({
    setValueRef: (text: string) => {
      if (inputRef.current) {
        inputRef.current.value = text;
      }
    },
  }));

  return (
    <Container>
      <ComponentIsVisible when={!!label}>
        <Label>{label}</Label>
      </ComponentIsVisible>
      <FieldContainer
        backgroundColor={backgroundColor}
        hasIcon={!!IconInput}
        height={height}
        isErrored={hasError}
        isFilled={isFilled}
        isFocused={isFocused}
        isReadOnly={readOnly}
        isSecure={secure}
      >
        {IconInput && <IconInput size={18} />}
        <Input
          isLoading={isLoading}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          readOnly={readOnly || isLoading}
          ref={inputRef}
          textAlign={textAlign}
          title={title}
          type={inputType}
          {...rest}
        />

        <ComponentIsVisible when={secure}>
          <ComponentIsVisible when={inputType === 'password'}>
            <ToggleSecure onClick={handleToggleSecure}>
              <FiEye size={18} />
            </ToggleSecure>
          </ComponentIsVisible>
          <ComponentIsVisible when={inputType !== 'password'}>
            <ToggleSecure onClick={handleToggleSecure}>
              <FiEyeOff size={18} />
            </ToggleSecure>
          </ComponentIsVisible>
        </ComponentIsVisible>

        <ComponentIsVisible when={hasError && !readOnly}>
          <Error title={errorMessage as string}>
            <FiAlertCircle size={18} />
          </Error>
        </ComponentIsVisible>
      </FieldContainer>
    </Container>
  );
};

export default forwardRef(ComponentInputBase);
